import { Link } from "react-router-dom";
import { WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { MediaTile, TypePill } from "@/components/admin/ReviewMedia";
import { mediaUrl, selfieUrl } from "@/lib/api";
import { formatDate } from "@/lib/format";

// Onboarding verification: the live selfie next to the user's uploaded profile photos, to compare faces.
export default function VerificationCard({ r, onDecide }) {
  const { data, loading } = useAdminLoad(`/admin/users/${r.user_id}`);
  const photos = data?.photos || [];
  const name = data?.profile?.first_name;
  return (
    <Panel className={`p-6 ${r.possible_ban_evasion ? "ring-2 ring-destructive/40" : ""}`} data-testid={`review-card-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <TypePill r={r} />
        {r.possible_ban_evasion && <Pill tone="error" className="px-3 py-1 text-sm" data-testid={`review-ban-evasion-${r.id}`}><WarningOctagon size={14} weight="fill" />Possible ban evasion</Pill>}
        <span className="cw-muted text-xs">{formatDate(r.created_at)}</span>
        {r.face_match != null && <Pill tone="neutral">Face match: {String(r.face_match)}</Pill>}
      </div>
      <p className="mt-3 text-lg font-medium">
        {name || "No name yet"}{data?.profile?.age ? `, ${data.profile.age}` : ""} <span className="cw-muted text-sm tabular-nums">· {r.new_phone}</span>
      </p>
      <p className="cw-soft mt-1 text-sm">Does the selfie match the person in the profile photos?</p>
      <div className="mt-5 grid gap-6 md:grid-cols-[200px_1fr]" data-testid={`review-compare-${r.id}`}>
        <div>
          <p className="mb-2 text-xs font-medium">Verification selfie</p>
          <MediaTile src={selfieUrl(r.selfie_url)} alt="Verification selfie" testId={`review-selfie-${r.id}`} className="cw-ring-warning rounded-2xl" />
        </div>
        <div>
          <p className="mb-2 text-xs font-medium">Profile photos {photos.length ? `(${photos.length})` : ""}</p>
          {loading ? <div className="h-32 animate-pulse rounded-2xl bg-[var(--cw-surface-tertiary)]" />
            : photos.length ? (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid={`review-photos-${r.id}`}>
                {photos.map((p, i) => <MediaTile key={p.id} src={mediaUrl(p.url)} alt={`Profile photo ${i + 1}`} label={p.is_main ? "Main" : `#${i + 1}`} testId={`review-photo-${r.id}-${i}`} />)}
              </div>
            ) : <p className="cw-muted text-sm">No profile photos uploaded.</p>}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-border pt-5">
        {r.status !== "pending" ? (
          <p className="text-sm"><Pill tone={r.status === "approved" ? "success" : "error"} className="capitalize">{r.status}</Pill>
            <span className="cw-muted ml-2">by {r.reviewed_by} · {formatDate(r.reviewed_at)}</span>{r.decision_note && <span className="cw-soft ml-2">“{r.decision_note}”</span>}</p>
        ) : (
          <>
            <Button onClick={() => onDecide(r, "approve")} data-testid={`review-approve-button-${r.id}`}>Approve</Button>
            <Button variant="outline" onClick={() => onDecide(r, "reject")} data-testid={`review-reject-button-${r.id}`}>Reject</Button>
          </>
        )}
        <Link to={`/admin/users/${r.user_id}`} className="cw-brand-deep ml-auto rounded text-sm underline-offset-4 hover:underline" data-testid={`review-user-link-${r.id}`}>View full profile</Link>
      </div>
    </Panel>
  );
}
